import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  Page,
  Text,
  BlockStack,
  Card,
  Select,
  InlineStack,
  Banner,
  Icon,
  Link,
  Checkbox,
  IndexTable,
  Box,
  Button,
  TextField,
} from "@medentic/polaris-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import {
  CheckCircleIcon,
  AlertCircleIcon,
  NoteIcon,
  PlayIcon,
} from "@medentic/polaris-icons";
import { ROOTS, CLINIC_ROUTES } from "@app/config/constants";
import { AppPageWrapper } from "@shared/ui";
import { GroupData } from "../../types";

interface ColumnMapping {
  source?: string;
  destination: string;
  mode?: string;
  value?: string;
  preview?: string[];
  dateFormat?: string;
}

interface ImportConfiguration {
  hasHeader: boolean;
  columnMappings: ColumnMapping[];
}

interface ImportMetadata {
  columnModes: Array<{
    id: string;
    label: string;
    description: string;
  }>;
  flow: Array<{
    id: string;
    label: string;
    description: string;
    helpLink: string;
    icon: string;
  }>;
}

interface ImportData {
  id: string;
  next: string;
  file: {
    type: string;
    filename: string;
    size: number;
    rows: number;
  };
  configuration: ImportConfiguration;
}

function ImportConfigurationPage() {
  const { t, i18n } = useTranslation();
  const { clinicId, importId } = useParams<{
    clinicId: string;
    importId: string;
  }>();
  const navigate = useNavigate();
  const { GET, PUT } = useApiRequest();

  // State definitions
  const [hasHeaderOverride, setHasHeaderOverride] = useState<boolean | null>(
    null
  );
  const [localConfigurationDraft, setLocalConfigurationDraft] =
    useState<ImportConfiguration | null>(null);

  // Queries and mutations
  const { data: importData } = useQuery<ImportData>({
    queryKey: ["import", importId],
    queryFn: () => GET(`/api/imports/${importId}`),
  });

  const { data: metadata } = useQuery<ImportMetadata>({
    queryKey: ["import-metadata"],
    queryFn: () => GET("/api/metadata/imports"),
  });

  const { data: schemaData } = useQuery<GroupData[]>({
    queryKey: ["patient-schema"],
    queryFn: () => GET(`/api/healthcare-companies/${clinicId}/patients/schema`),
  });

  const localConfiguration =
    localConfigurationDraft ?? importData?.configuration ?? null;
  const hasHeader = hasHeaderOverride ?? localConfiguration?.hasHeader ?? true;

  const columnModes = useMemo(() => {
    return (
      metadata?.columnModes?.map((mode) => ({
        label: mode.label,
        value: mode.id,
        helpText: mode.description,
      })) || []
    );
  }, [metadata]);

  const availableFields = useMemo(() => {
    const fields = [{ label: t("patients.import.dontImport"), value: "" }];

    // Add schema fields
    schemaData?.forEach((schema) => {
      schema.groups.forEach((group) => {
        group.properties.forEach((prop) => {
          // Only add fields that are not read-only and not hidden
          if (!prop.isReadOnlyValue && !prop.isHiddenDefinition) {
            fields.push({
              label: prop.label,
              value: prop.name,
            });
          }
        });
      });
    });

    return fields;
  }, [schemaData, t]);

  // Save configuration mutation
  const configurationMutation = useMutation({
    mutationFn: (config: ImportConfiguration) =>
      PUT(`/api/imports/${importId}/configuration`, { configuration: config }),
  });

  // Validate configuration
  const validateMutation = useMutation({
    mutationFn: () => GET(`/api/imports/${importId}/validate`),
  });

  // Update handlers to modify local state instead of API calls
  const handleFieldChange = useCallback(
    (source: string, destination: string) => {
      if (!localConfiguration) return;

      const newMappings = localConfiguration.columnMappings.map((mapping) => {
        if (mapping.source === source) {
          return { ...mapping, destination };
        }
        return mapping;
      });

      setLocalConfigurationDraft({
        ...localConfiguration,
        columnMappings: newMappings,
      });
    },
    [localConfiguration]
  );

  const handleDateFormatChange = useCallback(
    (source: string, dateFormat: string) => {
      if (!localConfiguration) return;

      const newMappings = localConfiguration.columnMappings.map((mapping) => {
        if (mapping.source === source) {
          return { ...mapping, dateFormat };
        }
        return mapping;
      });

      setLocalConfigurationDraft({
        ...localConfiguration,
        columnMappings: newMappings,
      });
    },
    [localConfiguration]
  );

  const handleModeChange = useCallback(
    (source: string, mode: string) => {
      if (!localConfiguration) return;

      const newMappings = localConfiguration.columnMappings.map((mapping) => {
        if (mapping.source === source) {
          return { ...mapping, mode };
        }
        return mapping;
      });

      setLocalConfigurationDraft({
        ...localConfiguration,
        columnMappings: newMappings,
      });
    },
    [localConfiguration]
  );

  // Update handleSave to use localConfiguration
  const handleSave = useCallback(() => {
    if (!localConfiguration) return;

    // Create new mappings without preview data
    const mappings = localConfiguration.columnMappings.map(
      ({ preview: _preview, ...rest }) => {
        return {
          source: rest.destination, // for save call source is destination from GET call
          destination: rest.source ?? "", // for save call destination is source from GET call
          mode: rest.mode,
          dateFormat: rest.dateFormat,
        };
      }
    );

    configurationMutation.mutate({
      columnMappings: mappings,
      hasHeader,
    });
  }, [localConfiguration, hasHeader, configurationMutation]);

  const unmappedCount = useMemo(() => {
    return (
      localConfiguration?.columnMappings.filter((m) => !m.destination).length ||
      0
    );
  }, [localConfiguration]);

  const handleClose = () => {
    navigate(`${ROOTS.CLINIC}/${clinicId}/${CLINIC_ROUTES.PATIENTS}`);
  };

  const rowMarkup = useMemo(() => {
    return (
      localConfiguration?.columnMappings.map((mapping, index) => (
        <IndexTable.Row
          id={mapping.source || ""}
          key={mapping.source || ""}
          position={index}
        >
          <IndexTable.Cell>
            <Icon
              source={mapping.destination ? CheckCircleIcon : AlertCircleIcon}
              tone={mapping.destination ? "success" : "caution"}
            />
          </IndexTable.Cell>
          <IndexTable.Cell>{mapping.source || ""}</IndexTable.Cell>
          <IndexTable.Cell>
            <BlockStack gap="0">
              {mapping.preview?.map((preview, index) => (
                <Text key={index} as="span" tone="subdued">
                  {preview}
                </Text>
              ))}
            </BlockStack>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Select
              label={""}
              options={availableFields}
              value={mapping.destination || ""}
              onChange={(value) =>
                handleFieldChange(mapping.source || "", value ?? "")
              }
            />
          </IndexTable.Cell>
          <IndexTable.Cell>
            {mapping.dateFormat !== undefined && (
              <TextField
                label={""}
                autoComplete="off"
                value={mapping.dateFormat}
                onChange={(value) =>
                  handleDateFormatChange(mapping.source || "", value)
                }
              />
            )}
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Select
              label={""}
              options={columnModes}
              value={mapping.mode || ""}
              onChange={(value) =>
                handleModeChange(mapping.source || "", value ?? "")
              }
            />
          </IndexTable.Cell>
        </IndexTable.Row>
      )) || []
    );
  }, [
    localConfiguration?.columnMappings,
    availableFields,
    columnModes,
    handleFieldChange,
    handleModeChange,
    handleDateFormatChange,
  ]);

  return (
    <AppPageWrapper>
      <Page
        fullWidth
        backAction={{ content: t("common.back"), onAction: handleClose }}
        title={t("patients.import.title")}
        secondaryActions={[
          {
            content: t("patients.import.importNewFile"),
            onAction: () =>
              navigate(
                `${ROOTS.CLINIC}/${clinicId}/${CLINIC_ROUTES.PATIENTS}?openImportModal=true`
              ),
          },
        ]}
      >
        <BlockStack gap="400">
          <InlineStack gap="400">
            <Link
              url={t("patients.import.helpUrl", { language: i18n.language })}
            >
              <InlineStack gap="200" blockAlign="center">
                <Icon source={NoteIcon} />
                {t("patients.import.help")}
              </InlineStack>
            </Link>
            <Link url={t("patients.import.videoUrl")}>
              <InlineStack gap="200" blockAlign="center">
                <Icon source={PlayIcon} />
                {t("patients.import.video")}
              </InlineStack>
            </Link>
          </InlineStack>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                {t("patients.import.fileDetails")}
              </Text>
              <Text as="p">
                {t("patients.import.filename")}: {importData?.file?.filename}
              </Text>
              <Text as="p">
                {t("patients.import.fileType")}: {importData?.file?.type}
              </Text>
              <Text as="p">
                {t("patients.import.fileSize")}:{" "}
                {((importData?.file?.size || 0) / 1024).toFixed(2)} KB
              </Text>
              <Text as="p">
                {t("patients.import.rows")}: {importData?.file?.rows}
              </Text>
              <Checkbox
                label={t("patients.import.hasHeader")}
                checked={hasHeader}
                onChange={(checked) => setHasHeaderOverride(checked)}
              />
            </BlockStack>
          </Card>

          <Card padding="0">
            <BlockStack gap="0">
              <Box
                padding="400"
                borderWidth="025"
                borderBlockStartWidth="025"
                borderColor="border"
              >
                <Text as="h2" variant="headingMd">
                  {t("patients.import.columnMapping")}
                </Text>
              </Box>
              <IndexTable
                itemCount={localConfiguration?.columnMappings.length || 0}
                headings={[
                  { title: t("patients.import.mapped") },
                  { title: t("patients.import.columnHeader") },
                  { title: t("patients.import.previewInfo") },
                  { title: t("patients.import.medenticProperty") },
                  { title: t("patients.import.format") },
                  { title: t("patients.import.action") },
                ]}
                selectable={false}
              >
                {rowMarkup}
              </IndexTable>
              <Box padding="400" borderWidth="025" borderColor="border">
                <InlineStack align="space-between" blockAlign="center">
                  {unmappedCount > 0 ? (
                    <Banner tone="warning">
                      <Text as="p">
                        {t("patients.import.unmappedColumns", {
                          count: unmappedCount,
                        })}
                      </Text>
                    </Banner>
                  ) : (
                    <div />
                  )}
                  <InlineStack gap="300">
                    <Button
                      onClick={() => validateMutation.mutate()}
                      loading={validateMutation.isPending}
                      disabled={true}
                    >
                      {t("patients.import.validate")}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleSave}
                      loading={configurationMutation.isPending}
                    >
                      {t("patients.import.import")}
                    </Button>
                  </InlineStack>
                </InlineStack>
              </Box>
            </BlockStack>
          </Card>
        </BlockStack>
      </Page>
    </AppPageWrapper>
  );
}

export default ImportConfigurationPage;
